import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    updateForceSignupGateStatus,
    updateShowSignInModalStatus,
    updateSignInModalSignUpStatus,
    updateSignUpOrigin,
} from "../redux/actions/navbar_action";
import { checkAuthentication } from "../redux/actions/user_action";

const SESSION_KEY = "visitorForceSignupSession";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

function createSessionState() {
    return {
        viewedJdpIds: [],
        jdpGateReached: false,
        lastActivityAt: Date.now(),
    };
}

function canUseLocalStorage() {
    return typeof window !== "undefined" && window.localStorage;
}

function readSessionState() {
    if (!canUseLocalStorage()) {
        return createSessionState();
    }

    try {
        const storedValue = localStorage.getItem(SESSION_KEY);

        if (!storedValue) {
            return createSessionState();
        }

        const parsedValue = JSON.parse(storedValue);

        const lastActivityAt = Number(parsedValue.lastActivityAt);
        const hasValidTimestamp = Number.isFinite(lastActivityAt);

        const hasExpired = !hasValidTimestamp || 
            Date.now() - lastActivityAt > SESSION_TIMEOUT_MS;
            
        if (hasExpired) {
            localStorage.removeItem(SESSION_KEY);
            return createSessionState();
        }

        return {
            viewedJdpIds: Array.isArray(parsedValue.viewedJdpIds)
                ? parsedValue.viewedJdpIds.map(String)
                : [],
            jdpGateReached: parsedValue.jdpGateReached === true,
            lastActivityAt,
        };
    } catch {
        localStorage.removeItem(SESSION_KEY);
        return createSessionState();
    }
}

function writeSessionState(state) {
    if (!canUseLocalStorage()) return;

    try {
        localStorage.setItem(
            SESSION_KEY,
            JSON.stringify({
                ...state,
                lastActivityAt: Date.now(),
            })
        );
    } catch {

    }
}

export default function useVisitorForceSignup() {
    const dispatch = useDispatch();
    const isEligibleVisitor = useCallback(() => {
        if (typeof window == "undefined") {
            return false;
        }

        return !checkAuthentication();
    }, []);

    const isForceSignupModalOpen = useSelector(
        (state) =>
            state?.navbar?.showSignInModal &&
            state?.navbar?.forceSignupGateActive
    );

    const closeForceSignupModal = useCallback(() => {
        if (!isForceSignupModalOpen) {
            return;
        }

        dispatch(updateShowSignInModalStatus(false));
        dispatch(updateSignInModalSignUpStatus(false));
        dispatch(updateSignUpOrigin(""));
        dispatch(updateForceSignupGateStatus(false));
    }, [dispatch, isForceSignupModalOpen]);

    const ShowSignupPrompt = useCallback(
        ({ 
            trigger, 
            pageNumber = null, 
            jobId = null, 
            jdpViewCount = null, 
            origin = null,
        }) => {
            if (!isEligibleVisitor()) {
                return false;
            }

            if (isForceSignupModalOpen) {
                return true;
            }

            dispatch(updateForceSignupGateStatus(true));
            dispatch(updateSignUpOrigin(origin || ""));
            dispatch(updateSignInModalSignUpStatus(true));
            dispatch(updateShowSignInModalStatus(true));

            return true;
        },
        [dispatch, isEligibleVisitor, isForceSignupModalOpen]
    );

    const checkJlpPageGate = useCallback(
        ({ pageNumber }) => {
            if (!pageNumber || pageNumber < 2) {
                closeForceSignupModal();
                return false;
            }

            return ShowSignupPrompt({
                trigger: "jlp_page_2",
                pageNumber,
            });
        },
        [ShowSignupPrompt, closeForceSignupModal]
    );

    const trackJdpViewGate = useCallback(
        ({ jobId, origin = null }) => {
            if (!jobId || typeof window === "undefined") {
                return false;
            }

            if (checkAuthentication()) {
                return false;
            }

            const state = readSessionState();
            const normalizedJobId = String(jobId);            
            const hasViewedJob = state.viewedJdpIds.includes(normalizedJobId);

            if (state.jdpGateReached) {
                return ShowSignupPrompt({
                    trigger: "jdp_6th_view_onwards",
                    jobId: normalizedJobId,
                    jdpViewCount: state.viewedJdpIds.length,
                    origin,
                });
            }

            const nextViewedJdpIds = hasViewedJob
                ? state.viewedJdpIds
                : [...state.viewedJdpIds, normalizedJobId];

            const nextViewCount = nextViewedJdpIds.length;

            if (nextViewCount >= 6) {
                writeSessionState({
                    ...state,
                    viewedJdpIds: nextViewedJdpIds,
                    jdpGateReached: true,
                });

                return ShowSignupPrompt({
                    trigger: "jdp_6th_view",
                    jobId: normalizedJobId,
                    jdpViewCount: nextViewCount,
                    origin,
                });
            }

            writeSessionState({
                ...state,
                viewedJdpIds: nextViewedJdpIds,
            });

            return false;
        },
        [ShowSignupPrompt]
    );

    return {
        checkJlpPageGate,
        trackJdpViewGate,
    };
}
